<script>
  import { External, M } from '../../external';
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { makeLmsRequest } from '../service/LmsClient';
  import { makeWebsiteRequest } from '../../Common/tools';
  import Loader from '../../Common/Loader.svelte';
  import { LmsCircleUtils } from './LmsCircleUtils';
  import { getLmsLocaleContext } from '../utils/LmsLocaleUtils';

  export let circleDetails, selectedPage, selectedGroup, isTemplate = false;

  let dispatch = createEventDispatcher();

  let grid = null;
  let builderMarkup = null;
  let originalMarkup = null;
  let originalWidgetsById = new Map();
  let loading = true;

  const walkTree = (node, callback) => {
    if (!node) {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((item) => walkTree(item, callback));
      return;
    }
    if (typeof node !== 'object') {
      return;
    }
    callback(node);
    Object.keys(node).forEach((key) => walkTree(node[key], callback));
  };

  const buildWidgetIndex = (widgets) => {
    const index = new Map();
    walkTree(widgets, (node) => {
      if (node && typeof node.id !== 'undefined' && node.id !== null && typeof node.type === 'string') {
        index.set(String(node.id), JSON.parse(JSON.stringify(node)));
      }
    });
    return index;
  };

  const getLocalizableFields = (type) => {
    const registered = (External.Widgets && External.Widgets.registeredWidgets) || [];
    for (let i = 0; i < registered.length; i++) {
      const def = registered[i] && registered[i].prototype;
      if (def && def.type === type && Array.isArray(def.localizableFields)) {
        return def.localizableFields;
      }
    }
    return [];
  };

  // Deterministic safety net: in a non-default locale the BASE (default-language) fields must
  // always equal the snapshot captured at load. The editor renders localized content baked into
  // the base (so the server produces the right HTML), so on save we restore each localizable
  // base field to its original default value. The localized edit itself lives in
  // data.locales[locale]; we ONLY fall back to the (baked) base value when no override exists yet
  // (i.e. an edit leaked straight into the base). An existing override is the source of truth and
  // must never be clobbered by the stale baked base. New widgets (no snapshot) are left as-is.
  const reconcileLocaleBaseValues = (widgets, localeContext) => {
    if (!localeContext || !localeContext.currentLocale || localeContext.currentLocale === localeContext.defaultLocale) {
      return;
    }
    const locale = localeContext.currentLocale;
    walkTree(widgets, (node) => {
      if (!node || typeof node !== 'object' || typeof node.type !== 'string' || typeof node.id === 'undefined' || node.id === null) {
        return;
      }
      const original = originalWidgetsById.get(String(node.id));
      if (!original) {
        return;
      }
      getLocalizableFields(node.type).forEach((field) => {
        if (typeof field !== 'string') {
          return;
        }
        const originalBase = original[field];
        const currentBase = node[field];
        if (JSON.stringify(currentBase) === JSON.stringify(originalBase)) {
          return;
        }
        const hasOverride = node.locales && node.locales[locale]
          && Object.prototype.hasOwnProperty.call(node.locales[locale], field);
        if (!hasOverride) {
          // The edit went straight into the base (no override recorded) — capture it as the
          // locale override before restoring the base.
          node.locales = node.locales && typeof node.locales === 'object' ? node.locales : {};
          node.locales[locale] = node.locales[locale] && typeof node.locales[locale] === 'object' ? node.locales[locale] : {};
          node.locales[locale][field] = currentBase;
        }
        // Restore the default-language base from the load-time snapshot. The override (the real
        // localized edit) is kept untouched.
        node[field] = originalBase;
      });
    });
  };

  let setupGrid = (node) => {
    const localeContext = getLmsLocaleContext();
    const isTranslating = localeContext.currentLocale && localeContext.currentLocale !== localeContext.defaultLocale;
    if (External.Website) {
      External.Website.defaultEditLocale = localeContext.defaultLocale;
      External.Website.currentEditLocale = localeContext.currentLocale;
    }
    // Keep the cookie in sync so any other editor surface (and the shared widget machinery's
    // fallback locale resolution) agrees on the active locale and can't reset it to default.
    if (window.Cookies && window.Cookies.set) {
      window.Cookies.set('current-locale', localeContext.currentLocale, { expires: 5 * 365, path: '/' });
    }
    let gridContainer = External.jQuery(node);
    // The circle grid renders outside .working-area-wrapper, so mark the actual container
    // with the active locale. Widgets.editorContext.getLocale() reads currentEditLocale first,
    // but data-edit-locale keeps the container self-describing for any scoped lookups.
    const localeClass = 'locale-' + (localeContext.currentLocale || '').replace('_', '-').toLowerCase();
    let classes = (gridContainer.attr('class') || '').split(/\s+/).filter((name) => name && name.indexOf('locale-') !== 0);
    classes.push(localeClass);
    gridContainer.attr('class', classes.join(' '));
    gridContainer.attr('data-edit-locale', localeContext.currentLocale);
    if (External.$) {
      External.$('body').toggleClass('translation-mode', isTranslating);
    }

    grid = External.Grid(gridContainer, {
      disableHistoryKeyboardBindings: false,
      forceDesktopMode: true,
      //widgetsOnly: true,
      showNewWidgetUiAtRight: false,//inPopup,
      hasAnchorPoints: false,
      historyButtons: '#lmsGridHistoryButtons',
      widgetFilter: (item) => {
        if (
          (item.type === External.WIDGET_TYPE.CATEGORY && item.value === M.WIDGET_CATEGORY_COMPLEX) ||
          (item.type === External.WIDGET_TYPE.WIDGET && item.value === 'LmsWidget') ||
          item.type === External.WIDGET_TYPE.COMPLEX_WIDGET
        ) {
          return false;
        }
        return true;
      },
      onChange: () => {
        if (grid) {
          dispatch('change');
        }
      },/*
      getContainerFixedPositionOffset: (container) => {
        return {
          left: container.offset().left,
          top: container.offset().top
        };
      }*/
    });
    // The markup was server-rendered in the DEFAULT locale (base content), so when editing a
    // non-default locale we must re-render each widget to surface its data.locales[locale]
    // overrides. The grid has just bound data('object') from the markup, so refresh now.
    if (isTranslating && External.Widgets && External.Widgets.locales && External.Widgets.locales.refreshAllForLocaleChange) {
      External.Widgets.locales.refreshAllForLocaleChange(gridContainer);
    }
    return {
      destroy: () => {
        grid.destroy();
        grid = null;
        gridContainer = null;
      }
    };
  };

  let addWidget = (event) => {
    if (grid) {
      grid.showAddWidgetDialog(event);
    }
  };

  let showPageSettingsFromCircle = () => {
    dispatch('settings');
  };

  let showPageAnalytics = () => {
    LmsCircleUtils.showAnalytics(circleDetails, selectedPage, 'PAGE');
  };

  let showPreview = () => {
    //selectedGroup?.pageGroup might be another group, not the group of the currently active page
    let group = null;
    if (selectedPage.groupId) {
      group = circleDetails.groups.find(group => group.pageGroup.id === selectedPage.groupId)?.pageGroup;
    }
    LmsCircleUtils.preview(circleDetails.circle, group, selectedPage, isTemplate);
  };

  let loadPageWidgets = async () => {
    if (builderMarkup) {
      return;
    }

    let response = await makeLmsRequest('/page/' + selectedPage.id + '/widgets', 'GET', null, null, { includeLocale: false });
    let pageContent = (await response.json());
    let widgets = pageContent.content || '[]';
    try {
      originalWidgetsById = buildWidgetIndex(JSON.parse(widgets));
    } catch (ignore) {
      originalWidgetsById = new Map();
    }
    // First try to replace any existing circleId
    widgets = widgets.replace(
      /"circleId":"[a-zA-Z0-9-]+"/g,
      `"circleId":"${circleDetails.circle.id}"`
    );
    widgets = widgets.replaceAll(
      '"type":',
      `"circleId":"${circleDetails.circle.id}","type":`
    );

    const localeContext = getLmsLocaleContext();
    const renderPayload = {
      withGlobalStyles: true,
      widgets: widgets
    };
    if (localeContext.currentLocale && localeContext.currentLocale !== localeContext.defaultLocale) {
      renderPayload.locale = localeContext.currentLocale;
    }
    response = await makeWebsiteRequest({
      action: 'renderWidgets',
      method: 'post',
      data: renderPayload
    });
    builderMarkup = originalMarkup = response.data;
  };

  export const save = async (showConfirmationMessage = true) => {
    const localeContext = getLmsLocaleContext();
    // Re-assert the active locale so the shared widget editor's locale-aware export routes
    // fields to the correct bucket even if global edit state drifted while this page was open.
    if (External.Website) {
      External.Website.defaultEditLocale = localeContext.defaultLocale;
      External.Website.currentEditLocale = localeContext.currentLocale;
    }
    let saveWidgets = grid.getSaveObject().widgets || [];
    // Belt-and-suspenders: ensure no localized edit bled into the default-language base fields.
    reconcileLocaleBaseValues(saveWidgets, localeContext);
    let widgets = JSON.stringify(saveWidgets);
    let response = await makeLmsRequest('/page/' + selectedPage.id + '/widgets', 'PUT', {
      pageId: selectedPage.id,
      content: widgets,
      locale: localeContext.currentLocale
    }, null, { includeLocale: false });
    if (response.ok) {
      if (showConfirmationMessage) {
        External.Tools.showSaveSuccessWindow();
      }
      originalMarkup = builderMarkup;
    }
    return response.ok;
  }

  export const discard = () => {
    loading = true;
    builderMarkup = null;
    // makes it look nicer when we introduce a delay
    setTimeout(() => {
      builderMarkup = originalMarkup;
      loading = false;
    }, 600);
  }

  export const undo = () => {
    if (grid) {
      grid.undo();
    }
  }

  export const redo = () => {
    if (grid) {
      grid.redo();
    }
  }

  const publishPage = () => {
    dispatch('publish');
  }

  onMount(async () => {
    loading = true;
    await loadPageWidgets();
    loading = false;
  });

  onDestroy(() => {
    builderMarkup = null;
  });
</script>
{#if loading}
  <Loader />
{:else}
  <div class="col20 flex flex-col">
    {#if selectedPage.hidden}
      <div class="col20 f-left red-bg2 white-text circle-page-floating-controls-outer">
        <div class="circle-page-floating-controls flex flex-row align-center justify-center">
          <div class="flex align-center">
            <span class="inline-block">{M.THIS_PAGE_IS_HIDDEN}</span>
            <a on:click={publishPage} class="small-button green rounded-effect">{M.PUBLISH}</a>
          </div>
        </div>
      </div>
    {/if}

    <div class="circle-side-controls">
      <a on:click={(event) => addWidget(event)}>
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000">
          <path d="M0 0h24v24H0V0z" fill="none" />
          <path
            d="M11 15h2v-3h3v-2h-3V7h-2v3H8v2h3zM21 3H3c-1.11 0-2 .89-2 2v12c0 1.1.89 2 2 2h5v2h8v-2h5c1.1 0 2-.9 2-2V5c0-1.11-.9-2-2-2zm0 14H3V5h18v12z" />
        </svg>
      </a>
      <a on:click={showPageSettingsFromCircle}>
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000">
          <path d="M0 0h24v24H0V0z" fill="none" />
          <path
            d="M19.43 12.98c.04-.32.07-.64.07-.98 0-.34-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.09-.16-.26-.25-.44-.25-.06 0-.12.01-.17.03l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.06-.02-.12-.03-.18-.03-.17 0-.34.09-.43.25l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.09.16.26.25.44.25.06 0 .12-.01.17-.03l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.06.02.12.03.18.03.17 0 .34-.09.43-.25l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zm-1.98-1.71c.04.31.05.52.05.73 0 .21-.02.43-.05.73l-.14 1.13.89.7 1.08.84-.7 1.21-1.27-.51-1.04-.42-.9.68c-.43.32-.84.56-1.25.73l-1.06.43-.16 1.13-.2 1.35h-1.4l-.19-1.35-.16-1.13-1.06-.43c-.43-.18-.83-.41-1.23-.71l-.91-.7-1.06.43-1.27.51-.7-1.21 1.08-.84.89-.7-.14-1.13c-.03-.31-.05-.54-.05-.74s.02-.43.05-.73l.14-1.13-.89-.7-1.08-.84.7-1.21 1.27.51 1.04.42.9-.68c.43-.32.84-.56 1.25-.73l1.06-.43.16-1.13.2-1.35h1.39l.19 1.35.16 1.13 1.06.43c.43.18.83.41 1.23.71l.91.7 1.06-.43 1.27-.51.7 1.21-1.07.85-.89.7.14 1.13zM12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
        </svg>
      </a>
      {#if !isTemplate}
        <a on:click={showPageAnalytics}>
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
            <path
              d="M280-280h80v-200h-80v200Zm320 0h80v-400h-80v400Zm-160 0h80v-120h-80v120Zm0-200h80v-80h-80v80ZM200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm0-560v560-560Z" />
          </svg>
        </a>
      {/if}
      <a on:click={showPreview}>
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000">
          <path d="M0 0h24v24H0V0z" fill="none" />
          <path
            d="M12 6c3.79 0 7.17 2.13 8.82 5.5C19.17 14.87 15.79 17 12 17s-7.17-2.13-8.82-5.5C4.83 8.13 8.21 6 12 6m0-2C7 4 2.73 7.11 1 11.5 2.73 15.89 7 19 12 19s9.27-3.11 11-7.5C21.27 7.11 17 4 12 4zm0 5c1.38 0 2.5 1.12 2.5 2.5S13.38 14 12 14s-2.5-1.12-2.5-2.5S10.62 9 12 9m0-2c-2.48 0-4.5 2.02-4.5 4.5S9.52 16 12 16s4.5-2.02 4.5-4.5S14.48 7 12 7z" />
        </svg>
      </a>
    </div>

    <div class="circle-content-wrapper"
         style:max-height={circleDetails.circle.styling.fixToViewportHeight ? (circleDetails.circle.styling.viewportHeight) + 'vh' : 'none'}>

      <div class="circle-content theme">
        {#key builderMarkup}
          {#if builderMarkup}
            <div class="desktop-view flex flex-col" use:setupGrid>
              {@html builderMarkup}
            </div>
          {/if}
        {/key}
      </div>
    </div>
  </div>
{/if}
